import {ReactNode, useMemo} from 'react';
import {
  Contact,
  FileText,
  Mail,
  Phone,
  ScanFace,
  ScanLine,
  UserRoundCheck,
} from 'lucide-react';

export type VerificationStepData = {
  icon: ReactNode;
  text: string;
  title?: string;
  description?: string;
};

type UseGetVerificationStepsParams = {
  biomatch: boolean;
  isLivenessDetectionEnabled?: boolean;
  isEmailEnabled?: boolean;
  isPhoneEnabled?: boolean;
  isNeedContactsVerification?: boolean;
  subject?: 'self' | 'other';
};

export const useGetVerificationSteps = ({
  biomatch,
  isLivenessDetectionEnabled = false,
  isEmailEnabled = false,
  isPhoneEnabled = false,
  isNeedContactsVerification = false,
  subject = 'self',
}: UseGetVerificationStepsParams): VerificationStepData[] => {
  return useMemo(() => {
    const isOther = subject === 'other';
    const confirmIdentity = {
      title: isOther ? 'confirm_its_them' : 'confirm_identity',
      description: isOther
        ? 'confirm_identity_description_their'
        : 'confirm_identity_description',
    };
    const VERIFICATION_STEPS = {
      scanDoc: {
        icon: <ScanLine />,
        text: isOther ? 'scan_their_document' : 'scan_your_document',
        description: 'scan_document_description',
      },
      takeSelfie: {
        icon: <UserRoundCheck />,
        text: 'take_selfie',
        ...confirmIdentity,
      },
      checkFace: {
        icon: <ScanFace />,
        text: 'check_face',
        ...confirmIdentity,
      },
      selectDoc: {
        icon: <FileText />,
        text: 'select_document',
        description: 'select_document_description',
      },
      emailPhone: {
        icon: <Contact />,
        text: 'verify_contacts',
        description: 'verify_contacts_description',
      },
      phone: {
        icon: <Phone />,
        text: 'verify_phone',
        description: 'verify_phone_description',
      },
      email: {
        icon: <Mail />,
        text: 'verify_email',
        description: 'verify_email_description',
      },
    };

    const steps: VerificationStepData[] = [];
    if (isNeedContactsVerification) {
      if (isEmailEnabled && isPhoneEnabled) {
        steps.push(VERIFICATION_STEPS.emailPhone);
      } else if (isEmailEnabled) {
        steps.push(VERIFICATION_STEPS.email);
      } else if (isPhoneEnabled) {
        steps.push(VERIFICATION_STEPS.phone);
      }
    }

    if (biomatch) {
      steps.push(
        VERIFICATION_STEPS.scanDoc,
        isLivenessDetectionEnabled
          ? VERIFICATION_STEPS.checkFace
          : VERIFICATION_STEPS.takeSelfie,
      );
    } else {
      steps.push(VERIFICATION_STEPS.selectDoc, VERIFICATION_STEPS.scanDoc);
    }

    return steps;
  }, [
    biomatch,
    isLivenessDetectionEnabled,
    isEmailEnabled,
    isPhoneEnabled,
    isNeedContactsVerification,
    subject,
  ]);
};
